import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/lib/components/Dialog";
import {
  useSelfQuery,
  useSubmitOnboardingSurveyMutation,
} from "@/lib/queries/user-queries";
import {
  getQuestion,
  SurveyQuestion,
  SurveyQuestionOption,
} from "./onboarding-survey-questions";
import { OnboardingSurveyAnswerDto } from "@repo/api/user";
import { Button } from "@/lib/components/Button/Button";
import { useConfetti } from "@/lib/components/Confetti";

export default function OnboardingSurveyDialog() {
  const { data: self } = useSelfQuery();

  if (!self) {
    return null;
  }

  return (
    <OnboardingSurveyDialogInternal
      onboardingSurveyCompleted={self.onboardingSurveyCompleted}
    />
  );
}

function OnboardingSurveyDialogInternal({
  onboardingSurveyCompleted,
}: {
  onboardingSurveyCompleted: boolean;
}) {
  const [isOpen, setIsOpen] = useState(!onboardingSurveyCompleted);
  const submitted = useRef(false);

  const { mutate: submitOnboardingSurvey } =
    useSubmitOnboardingSurveyMutation();
  const [answers, setAnswers] = useState<OnboardingSurveyAnswerDto[]>([]);

  const handleSubmit = useCallback(
    (finished: boolean) => {
      if (submitted.current) {
        return;
      }

      submitted.current = true;

      submitOnboardingSurvey({
        finished,
        answers: answers,
      });
    },
    [answers, submitOnboardingSurvey],
  );

  const handleClose = useCallback(() => {
    handleSubmit(false);
    setIsOpen(false);
  }, [handleSubmit]);

  const handleAnswer = useCallback(
    (answer: OnboardingSurveyAnswerDto) => {
      setAnswers([...answers, answer]);
    },
    [answers],
  );

  const handleBack = useCallback(() => {
    setAnswers(answers.slice(0, -1));
  }, [answers]);

  const question = getQuestion(answers);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/85" />

        {question ? (
          <OnboardingSurveyQuestion
            question={question}
            numAnswers={answers.length}
            onAnswer={handleAnswer}
            onBack={handleBack}
          />
        ) : (
          <OnboardingSurveyCompleted
            onSubmit={handleSubmit}
            setIsOpen={setIsOpen}
          />
        )}
      </DialogPortal>
    </Dialog>
  );
}

type OnboardingSurveyQuestionProps = {
  question: SurveyQuestion;
  numAnswers: number;
  onAnswer: (answer: OnboardingSurveyAnswerDto) => void;
  onBack: () => void;
};

const OnboardingSurveyQuestion = forwardRef<
  HTMLDivElement,
  OnboardingSurveyQuestionProps
>(
  (
    { question, numAnswers, onAnswer, onBack }: OnboardingSurveyQuestionProps,
    ref,
  ) => {
    return (
      <DialogContent
        ref={ref}
        className="max-h-[90vh] w-200 max-w-full overflow-y-auto max-sm:px-0"
        showCloseButton={true}
      >
        <DialogTitle hidden>Onboarding survey</DialogTitle>
        <DialogDescription hidden>Onboarding survey</DialogDescription>

        <div className="flex h-full w-full flex-col items-center justify-center px-0 py-5 lg:px-10">
          <div className="flex w-full flex-col items-center justify-end px-6 lg:h-30">
            <h1 className="max-w-135 text-center text-3xl leading-snug font-semibold">
              {question.title}
            </h1>
            {numAnswers === 0 && (
              <h2 className="mt-2 text-center text-text-secondary">
                Your answers help personalize Amplifiles for you.
              </h2>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 pt-10 sm:gap-5 lg:grid-cols-4">
            {question.options.map((option) => (
              <AnswerOption
                key={option.value}
                questionId={question.id}
                option={option}
                onAnswer={onAnswer}
              />
            ))}
          </div>
          <div className="flex h-27 w-full items-end justify-center sm:justify-end">
            {numAnswers > 0 && (
              <Button variant="ghost" size="sm" onClick={onBack}>
                Back
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                onAnswer({
                  questionId: question.id,
                  answer: "",
                })
              }
            >
              Skip
            </Button>
          </div>
        </div>
      </DialogContent>
    );
  },
);

const confettiOptions = {
  particleCount: 30,
  spread: 70,
  startVelocity: 30,
  origin: {
    x: 0.5,
    y: 0.5,
  },
  colors: ["#f98c5e", "#f66c97", "#7979fb"],
};

type OnboardingSurveyCompletedProps = {
  onSubmit: (finished: boolean) => void;
  setIsOpen: (isOpen: boolean) => void;
};

const OnboardingSurveyCompleted = forwardRef<
  HTMLDivElement,
  OnboardingSurveyCompletedProps
>(({ onSubmit, setIsOpen }: OnboardingSurveyCompletedProps, ref) => {
  const confettiSource = useRef<HTMLHeadingElement>(null);
  const { showConfetti } = useConfetti(confettiOptions);

  useEffect(() => {
    let isMounted = true;

    onSubmit(true);
    showConfetti(confettiSource.current);

    setTimeout(() => {
      if (isMounted) {
        setIsOpen(false);
      }
    }, 4000);

    return () => {
      isMounted = false;
    };
  }, [onSubmit, setIsOpen, showConfetti]);

  return (
    <div
      ref={ref}
      className="absolute inset-0 flex items-center justify-center"
      onClick={() => setIsOpen(false)}
    >
      <div
        ref={confettiSource}
        className="z-50 flex h-full w-full animate-fade-in flex-col items-center justify-center"
      >
        <h1 className="text-center text-4xl font-bold text-white">
          Thank you!
        </h1>
        <p className="mt-1 animate-slide-in-from-top text-center text-xl font-light text-white delay-800">
          Time to create stunning videos
        </p>
      </div>
    </div>
  );
});

function AnswerOption({
  questionId,
  option,
  onAnswer,
}: {
  questionId: string;
  option: SurveyQuestionOption;
  onAnswer: (answer: OnboardingSurveyAnswerDto) => void;
}) {
  const dynamicClassName = option.imageUrl ? "h-[160px]" : "h-[40px]";

  return (
    <Button
      variant="outline"
      className={`w-[160px] ${dynamicClassName}`}
      onClick={() =>
        onAnswer({
          questionId: questionId,
          answer: option.value,
        })
      }
    >
      {option.imageUrl && (
        <>
          <img src={option.imageUrl} className="h-[60px] w-[101px]" />
          <div className="h-3"></div>
        </>
      )}
      {option.label}
    </Button>
  );
}
